import React, { useEffect, useMemo, useState } from 'react';
import { Chart } from 'react-google-charts';
import type { LabResultsItem, MarketQualitySpecItem, TestTypeDefinitionItem } from '../../contexts/tableDefinitions';
import {
    FIELD_LAB_PAIRS,
    describeGroupLocation,
    getPairFor,
    pivotLabResults,
    buildTestTypeDefMap,
    labelForTestType,
    unitForTestType,
    toLabDateYMD,
    formatLabDateLabel,
    labDateStartMs,
    labDateEndMs,
} from './labResultsHelpers';
import MarketContributorsModal from './MarketContributorsModal';

// A fresh test type opens on its N most-recent results, not a cramped all-time view.
const DEFAULT_LAST_N = 10;

const MARKET_COLORS = ['#0d1b3d', '#0d47a1', '#1b5e20', '#4a148c', '#b71c1c'];
const LEASE_COLORS = ['#1565c0', '#2e7d32', '#6a1b9a', '#ef6c00', '#c62828', '#00838f', '#ad1457', '#4e342e', '#283593', '#558b2f'];

// Lighten a hex color toward white (Field series = paler shade of its Lab series).
const lighten = (hex: string, amount: number): string => {
    const h = hex.replace('#', '');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    const mix = (c: number) => Math.round(c + (255 - c) * amount);
    return `#${[mix(r), mix(g), mix(b)].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
};

interface LabResultsGraphProps {
    tableData: LabResultsItem[];
    // Per market + test_type penalty/rejection limits.
    qualitySpecs?: MarketQualitySpecItem[];
    // Test-type catalog for labels/units/order (falls back to a humanized slug).
    definitions?: TestTypeDefinitionItem[];
}

// amber = Deduct start, red = Maximum.
const DEDUCT_LINE_COLOR = '#f9a825';
const MAXIMUM_LINE_COLOR = '#c62828';

// Over-limit style for a sample (`limit` null = no spec → never flagged).
const styleFor = (value: number | null, limit: number | null, kind: 'line' | 'bar') => {
    if (value == null || limit == null || value <= limit) return null;
    return kind === 'bar'
        ? 'color: #e53935'
        : 'point { size: 9; fill-color: #e53935; }';
};

const fmtNum = (n: number | null) => {
    if (n == null) return '—';
    return Math.abs(n) >= 100 ? n.toFixed(0) : n.toFixed(2).replace(/\.?0+$/, '');
};

const escapeHtml = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const percentile = (sorted: number[], p: number): number => {
    if (!sorted.length) return 0;
    const idx = Math.min(sorted.length - 1, Math.max(0, Math.floor((p / 100) * sorted.length)));
    return sorted[idx];
};

interface LocationInfo {
    label: string;
    isMarket: boolean;
    producer: string;
    marketId: number | null;
}

const LabResultsGraph: React.FC<LabResultsGraphProps> = ({ tableData, qualitySpecs = [], definitions = [] }) => {
    const groups = useMemo(() => pivotLabResults(tableData || []), [tableData]);
    const defMap = useMemo(() => buildTestTypeDefMap(definitions), [definitions]);

    // Dropdown options: Lab/Field pairs collapse into one entry, the rest use the
    // catalog label, ordered by display_order.
    const testTypeOptions = useMemo(() => {
        const present = new Set<string>();
        groups.forEach((g) => Object.keys(g.values).forEach((tt) => present.add(tt)));

        const orderOf = (tt: string) => defMap.get(tt)?.display_order ?? Number.MAX_SAFE_INTEGER;
        const categoryOf = (tt: string) => defMap.get(tt)?.category || 'Other';

        type Opt = { value: string; label: string; category: string; order: number; paired: boolean };
        const options: Opt[] = [];
        const consumed = new Set<string>();
        for (const pair of Object.values(FIELD_LAB_PAIRS)) {
            if (present.has(pair.lab) || present.has(pair.field)) {
                options.push({
                    value: pair.lab,
                    label: `${pair.label} (Lab vs Field)`,
                    category: categoryOf(pair.lab),
                    order: orderOf(pair.lab),
                    paired: true,
                });
                consumed.add(pair.lab);
                consumed.add(pair.field);
            }
        }
        for (const tt of Array.from(present)) {
            if (consumed.has(tt)) continue;
            options.push({
                value: tt,
                label: labelForTestType(tt, defMap),
                category: categoryOf(tt),
                order: orderOf(tt),
                paired: false,
            });
        }
        return options.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
    }, [groups, defMap]);

    // Options grouped by category for <optgroup>; flat list when there's only one.
    const optionGroups = useMemo(() => {
        const byCategory = new Map<string, typeof testTypeOptions>();
        for (const o of testTypeOptions) {
            if (!byCategory.has(o.category)) byCategory.set(o.category, []);
            byCategory.get(o.category)!.push(o);
        }
        return Array.from(byCategory.entries());
    }, [testTypeOptions]);
    const useOptgroups = optionGroups.length > 1;

    const [testType, setTestType] = useState<string>('');
    useEffect(() => {
        const values = testTypeOptions.map((o) => o.value);
        if (!testType && values.length) setTestType(values[0]);
        else if (testType && values.length && !values.includes(testType)) setTestType(values[0]);
    }, [testTypeOptions, testType]);

    const pair = useMemo(() => getPairFor(testType), [testType]);
    const unit = useMemo(() => unitForTestType(testType, defMap), [testType, defMap]);

    // Chart kind toggle (default to bar — clearer for value comparison)
    const [chartKind, setChartKind] = useState<'line' | 'bar'>('bar');

    // Y-axis controls
    const [scaleType, setScaleType] = useState<'linear' | 'log'>('linear');
    const [showOutliers, setShowOutliers] = useState<boolean>(false);
    // Reset outliers/scale when changing test_type so the new view starts clean
    useEffect(() => {
        setShowOutliers(false);
        setOverLimitPinned(false);
        setOverLimitHover(false);
    }, [testType]);

    // Date range: draft (typed) vs applied (filters the chart); "Apply" copies one
    // to the other, presets set both.
    const [dateFrom, setDateFrom] = useState<string>('');
    const [dateTo, setDateTo] = useState<string>('');
    const [appliedFrom, setAppliedFrom] = useState<string>('');
    const [appliedTo, setAppliedTo] = useState<string>('');
    // "Last N samples" cap, applied after the range filter. null = no cap.
    const [lastNSamples, setLastNSamples] = useState<number | null>(DEFAULT_LAST_N);

    // Date span for the current test type (drives presets + bounds).
    const dateExtent = useMemo(() => {
        const testTypesToPlot = pair ? [pair.lab, pair.field] : [testType];
        const distinctSet = new Set<number>();
        groups.forEach((g) => {
            if (!testTypesToPlot.some((tt) => g.values[tt] != null)) return;
            const t = new Date(g.test_date).getTime();
            if (!Number.isNaN(t)) distinctSet.add(t);
        });
        if (!distinctSet.size) return null;
        const distinct = Array.from(distinctSet).sort((a, b) => a - b);
        return {
            min: new Date(distinct[0]),
            max: new Date(distinct[distinct.length - 1]),
            distinct,
        };
    }, [groups, testType, pair]);

    // Reset the range when the test type changes — back to the default last-N view.
    useEffect(() => {
        setDateFrom('');
        setDateTo('');
        setAppliedFrom('');
        setAppliedTo('');
        setLastNSamples(DEFAULT_LAST_N);
        setAutoSelect(true);
    }, [testType]);

    const setRange = (from: string, to: string) => {
        setDateFrom(from);
        setDateTo(to);
        setAppliedFrom(from);
        setAppliedTo(to);
        setLastNSamples(null);
        setAutoSelect(true);
    };

    const applyPreset = (preset: '30d' | '90d' | 'ytd' | 'all' | 'lastN') => {
        if (preset === 'all') {
            setRange('', '');
            return;
        }
        if (preset === 'lastN') {
            setRange('', '');
            setLastNSamples(DEFAULT_LAST_N);
            return;
        }
        // Anchor to the most recent sample, not today (data may be historical).
        const anchor = dateExtent?.max ?? new Date();
        let from: Date;
        if (preset === 'ytd') {
            from = new Date(Date.UTC(anchor.getUTCFullYear(), 0, 1));
        } else {
            const days = preset === '30d' ? 30 : 90;
            from = new Date(anchor.getTime() - days * 24 * 60 * 60 * 1000);
        }
        setRange(toLabDateYMD(from), toLabDateYMD(anchor));
    };

    const applyDraftRange = () => {
        setAppliedFrom(dateFrom);
        setAppliedTo(dateTo);
        setLastNSamples(null);
        setAutoSelect(true);
    };

    const rangeDirty = dateFrom !== appliedFrom || dateTo !== appliedTo;

    const fromMs = useMemo(() => (appliedFrom ? labDateStartMs(appliedFrom) : null), [appliedFrom]);
    const toMs = useMemo(() => (appliedTo ? labDateEndMs(appliedTo) : null), [appliedTo]);
    const inRange = (t: number) => (fromMs == null || t >= fromMs) && (toMs == null || t <= toMs);

    // Locations available for the current testType
    const locationsForTest = useMemo<LocationInfo[]>(() => {
        const testTypesToPlot = pair ? [pair.lab, pair.field] : [testType];
        const info = new Map<string, LocationInfo>();
        groups.forEach((g) => {
            const hasValue = testTypesToPlot.some((tt) => g.values[tt] != null);
            if (!hasValue) return;
            const label = describeGroupLocation(g);
            const isMarket = !g.lease_id && (!!g.market_id || !!g.station_name);
            const producer = isMarket
                ? (g.station_name || 'Market')
                : (g.producer_name || 'No producer');
            if (!info.has(label)) {
                info.set(label, {
                    label,
                    isMarket,
                    producer,
                    marketId: isMarket && g.market_id != null ? Number(g.market_id) : null,
                });
            }
        });
        return Array.from(info.values()).sort((a, b) => {
            if (a.isMarket !== b.isMarket) return a.isMarket ? -1 : 1;
            return a.label.localeCompare(b.label);
        });
    }, [groups, testType, pair]);

    // inRangeLocations: locations with a result in the date range (drives which
    // checkboxes show). windowLocations: those also inside the Last-N window (drives
    // which start checked).
    const { inRangeLocations, windowLocations } = useMemo(() => {
        const testTypesToPlot = pair ? [pair.lab, pair.field] : [testType];
        const samples: { t: number; location: string }[] = [];
        groups.forEach((g) => {
            const t = new Date(g.test_date).getTime();
            if (Number.isNaN(t) || !inRange(t)) return;
            if (!testTypesToPlot.some((tt) => g.values[tt] != null)) return;
            samples.push({ t, location: describeGroupLocation(g) });
        });
        const inRangeSet = new Set(samples.map((s) => s.location));
        let windowSamples = samples;
        if (lastNSamples != null && samples.length > lastNSamples) {
            windowSamples = [...samples].sort((a, b) => b.t - a.t).slice(0, lastNSamples);
        }
        return { inRangeLocations: inRangeSet, windowLocations: new Set(windowSamples.map((s) => s.location)) };
    }, [groups, testType, pair, fromMs, toMs, lastNSamples]);

    // While `autoSelect` is on, the selection is derived from the current window;
    // hand-toggling a checkbox flips to manual mode (a preset / test type re-arms it).
    const [selected, setSelected] = useState<Record<string, boolean>>({});
    const [autoSelect, setAutoSelect] = useState<boolean>(true);
    useEffect(() => {
        if (!autoSelect) return;
        const next: Record<string, boolean> = {};
        locationsForTest.forEach((l) => { next[l.label] = windowLocations.has(l.label); });
        setSelected(next);
    }, [windowLocations, locationsForTest, autoSelect]);

    const [leasePanelOpen, setLeasePanelOpen] = useState<boolean>(false);
    const [search, setSearch] = useState<string>('');
    const [collapsedProducers, setCollapsedProducers] = useState<Record<string, boolean>>({});

    // Selected / total over the in-range locations, shown on the dropdown toggle.
    const leaseCounts = useMemo(() => {
        const inRange = locationsForTest.filter((l) => inRangeLocations.has(l.label));
        return { total: inRange.length, selected: inRange.filter((l) => selected[l.label]).length };
    }, [locationsForTest, inRangeLocations, selected]);

    // Toggle label that matches the current view (Markets / Leases).
    const locationNoun = useMemo(() => {
        if (!locationsForTest.length) return 'Leases';
        if (locationsForTest.every((l) => l.isMarket)) return 'Markets';
        if (locationsForTest.every((l) => !l.isMarket)) return 'Leases';
        return 'Locations';
    }, [locationsForTest]);

    // "Which leases contributed to this market" modal (data from crude_main_delivery)
    const [contributorsTarget, setContributorsTarget] = useState<{ marketId: number; label: string; anchorDate: string | null } | null>(null);

    // Over-limit list: hover shows a preview, click pins the full list open.
    const [overLimitHover, setOverLimitHover] = useState<boolean>(false);
    const [overLimitPinned, setOverLimitPinned] = useState<boolean>(false);

    // "See Quality Specs" toggle — the limit caps only render when this is on.
    const [showSpecs, setShowSpecs] = useState<boolean>(false);

    // Bar-mode limit caps drawn as an HTML overlay on each market's bar (Google Charts
    // can't clip a horizontal mark to one bar). Positioned from the chart layout on 'ready'.
    const [limitMarks, setLimitMarks] = useState<{ left: number; top: number; width: number; color: string; market: string; lineLabel: string }[]>([]);
    const [capTip, setCapTip] = useState<{ left: number; top: number; market: string; lineLabel: string; color: string } | null>(null);

    const selectAll = () => {
        const next: Record<string, boolean> = {};
        locationsForTest.forEach((l) => { next[l.label] = true; });
        setSelected(next);
        // "Select All" means show everything — drop the Last-N cap and enter manual
        // mode, otherwise the cap would re-hide the newly-checked leases.
        setLastNSamples(null);
        setAutoSelect(false);
    };
    const clearAll = () => {
        const next: Record<string, boolean> = {};
        locationsForTest.forEach((l) => { next[l.label] = false; });
        setSelected(next);
        setAutoSelect(false);
    };
    const selectAllInProducer = (producer: string) => {
        setLastNSamples(null);
        setAutoSelect(false);
        setSelected((prev) => {
            const next = { ...prev };
            locationsForTest
                .filter((l) => l.producer === producer)
                .forEach((l) => { next[l.label] = true; });
            return next;
        });
    };
    const clearAllInProducer = (producer: string) => {
        setAutoSelect(false);
        setSelected((prev) => {
            const next = { ...prev };
            locationsForTest
                .filter((l) => l.producer === producer)
                .forEach((l) => { next[l.label] = false; });
            return next;
        });
    };
    // Ticking a location drops the Last-N cap so it plots even if its data is older.
    const toggleLocation = (label: string, on: boolean) => {
        setAutoSelect(false);
        if (on && lastNSamples != null) setLastNSamples(null);
        setSelected((prev) => ({ ...prev, [label]: on }));
    };
    const toggleCollapseProducer = (producer: string) => {
        setCollapsedProducers((prev) => ({ ...prev, [producer]: !prev[producer] }));
    };

    // Group locations by producer (markets get a synthetic group at the top)
    const filteredGroups = useMemo(() => {
        const q = search.trim().toLowerCase();
        const matches = (l: LocationInfo) =>
            !q || l.label.toLowerCase().includes(q) || l.producer.toLowerCase().includes(q);

        const byProducer = new Map<string, { producer: string; isMarket: boolean; items: LocationInfo[] }>();
        for (const l of locationsForTest) {
            if (!inRangeLocations.has(l.label)) continue; // hide leases with no result in the current date range
            if (!matches(l)) continue;
            const key = (l.isMarket ? 'M::' : 'L::') + l.producer;
            if (!byProducer.has(key)) {
                byProducer.set(key, { producer: l.producer, isMarket: l.isMarket, items: [] });
            }
            byProducer.get(key)!.items.push(l);
        }
        return Array.from(byProducer.values()).sort((a, b) => {
            if (a.isMarket !== b.isMarket) return a.isMarket ? -1 : 1;
            return a.producer.localeCompare(b.producer);
        });
    }, [locationsForTest, search, inRangeLocations]);

    // Tooltip data per (date, location, testType)
    type TooltipFields = {
        producer: string | null;
        sample_point: string | null;
        sampled_by: string | null;
        note: string | null;
        conclusion: string | null;
    };

    const { chartData, hasData, seriesMeta, yAxisMax, observedMaxRaw, p95Value, stats, dateTicks, overLimitSamples, hasLimit, seriesSpec } = useMemo(() => {
        const testTypesToPlot = pair ? [pair.lab, pair.field] : [testType];
        const activeLocations = locationsForTest.filter((l) => selected[l.label]);

        // Empty state: Google Charts needs a typed placeholder row, not a bare header.
        const emptyResult = (label: string) => {
            const header: any[] = [
                { type: 'date', label: 'Test Date' },
                { type: 'number', label },
            ];
            const row: any[] = [new Date(), null];
            return {
                chartData: [header, row] as any[][],
                hasData: false,
                seriesMeta: [] as { isMarket: boolean; isField: boolean; location: string; marketId: number | null }[],
                yAxisMax: undefined as number | undefined,
                observedMaxRaw: 0,
                p95Value: 0,
                stats: { count: 0, min: null, max: null, avg: null, overLimit: 0, dateCount: 0 },
                dateTicks: [] as Date[],
                overLimitSamples: [] as { date: number; location: string; value: number }[],
                hasLimit: false,
                seriesSpec: [] as { deduct: number | null; max: number | null }[],
            };
        };

        const relevantGroups = groups.filter((g) => {
            if (!testTypesToPlot.some((tt) => g.values[tt] != null)) return false;
            return activeLocations.some((l) => l.label === describeGroupLocation(g));
        });

        if (!relevantGroups.length) return emptyResult('No data');

        // Per-market `maximum` spec for the active test_type (drives over-limit).
        const specForMarket = new Map<number, MarketQualitySpecItem>();
        (qualitySpecs || []).forEach((s) => {
            if (String(s.test_type) === String(testType)) specForMarket.set(Number(s.market_id), s);
        });
        // location → its market's maximum (null for leases / no-spec markets).
        const limitByLocation = new Map<string, number | null>();
        locationsForTest.forEach((l) => {
            const spec = l.marketId != null ? specForMarket.get(l.marketId) : undefined;
            const m = spec && spec.maximum != null ? Number(spec.maximum) : NaN;
            limitByLocation.set(l.label, Number.isNaN(m) ? null : m);
        });
        const limitFor = (location: string): number | null => limitByLocation.get(location) ?? null;
        const hasLimit = activeLocations.some((l) => limitFor(l.label) != null);

        // One entry per plotted sample (date × location × test_type) + a flat list
        // for the Last-N cap.
        const valueByKey = new Map<string, number>();
        const tooltipByKey = new Map<string, TooltipFields>();
        const samples: { t: number; location: string; tt: string; key: string }[] = [];
        relevantGroups.forEach((g) => {
            const label = describeGroupLocation(g);
            const t = new Date(g.test_date).getTime();
            if (Number.isNaN(t) || !inRange(t)) return;

            // Pull tooltip fields from the group's underlying rows
            const sampledByVals = new Set<string>();
            const noteVals = new Set<string>();
            (g.rows || []).forEach((r: any) => {
                if (r.sampled_by) sampledByVals.add(String(r.sampled_by));
                if (r.note) noteVals.add(String(r.note));
            });
            const tooltipFields: TooltipFields = {
                producer: g.producer_name || g.station_name || null,
                sample_point: g.sample_point || null,
                sampled_by: sampledByVals.size ? Array.from(sampledByVals).join(', ') : null,
                note: noteVals.size ? Array.from(noteVals).join(' | ') : null,
                conclusion: g.texts?.['conclusion'] || null,
            };

            testTypesToPlot.forEach((tt) => {
                const v = g.values[tt];
                if (v != null) {
                    const key = `${t}|${label}|${tt}`;
                    valueByKey.set(key, v);
                    tooltipByKey.set(key, tooltipFields);
                    samples.push({ t, location: label, tt, key });
                }
            });
        });

        // "Last N" cap: the set of (date,location,test) cells that may be drawn.
        let allowedKeys: Set<string> | null = null;
        if (lastNSamples != null && samples.length > lastNSamples) {
            const recent = [...samples].sort((a, b) => b.t - a.t).slice(0, lastNSamples);
            allowedKeys = new Set(recent.map((s) => s.key));
        }
        const isAllowed = (key: string) => allowedKeys == null || allowedKeys.has(key);

        // Distinct dates surviving the range filter + the Last-N cap.
        const dateSet = new Set<number>();
        samples.forEach((s) => { if (isAllowed(s.key)) dateSet.add(s.t); });
        const dates = Array.from(dateSet).sort((a, b) => a - b);

        if (!dates.length) return emptyResult('No data in range');

        type SeriesDesc = { label: string; location: string; testType: string; isField: boolean; isMarket: boolean };
        const seriesDescs: SeriesDesc[] = [];
        for (const loc of activeLocations) {
            for (const tt of testTypesToPlot) {
                // Skip series with no drawable value (avoids empty legend entries).
                const hasValue = dates.some((t) => {
                    const key = `${t}|${loc.label}|${tt}`;
                    return valueByKey.has(key) && isAllowed(key);
                });
                if (!hasValue) continue;
                const isField = !!pair && tt === pair.field;
                const suffix = pair ? (isField ? ' — Field' : ' — Lab') : '';
                seriesDescs.push({
                    label: `${loc.label}${suffix}`,
                    location: loc.label,
                    testType: tt,
                    isField,
                    isMarket: loc.isMarket,
                });
            }
        }

        // Market id for each location label — used to look up its quality spec.
        const marketIdByLabel = new Map<string, number | null>();
        locationsForTest.forEach((l) => marketIdByLabel.set(l.label, l.marketId));

        // Bar mode uses the date as a discrete string category (even, full-width bars);
        // line mode keeps a continuous date axis.
        const isBarMode = chartKind === 'bar';

        // Per-series deduct/max limits, drawn as overlay caps in bar mode (see the
        // 'ready' handler). Null for leases and markets without a spec.
        const numOrNull = (v: any): number | null => {
            if (v == null) return null;
            const n = Number(v);
            return Number.isNaN(n) ? null : n;
        };
        const seriesSpec = seriesDescs.map((s) => {
            if (!s.isMarket) return { deduct: null as number | null, max: null as number | null };
            const mid = marketIdByLabel.get(s.location);
            const spec = mid != null ? specForMarket.get(mid) : undefined;
            return { deduct: numOrNull(spec?.deduct_start), max: numOrNull(spec?.maximum) };
        });

        const header: any[] = [isBarMode ? { type: 'string', label: 'Test Date' } : { type: 'date', label: 'Test Date' }];
        seriesDescs.forEach((s) => {
            // Declare the column as number explicitly — an all-null series would
            // otherwise infer as string and break ("All series must be the same type").
            header.push({ type: 'number', label: s.label });
            header.push({ role: 'style' });
            header.push({ role: 'tooltip', type: 'string', p: { html: true } });
        });

        // Log scale can't render 0/negatives, so plot them at a floor (tooltip keeps
        // the true value) — otherwise those bars vanish.
        const useLogScale = scaleType === 'log';
        const LOG_FLOOR = 0.01;

        const allValues: number[] = [];
        let observedMax = 0;
        const body = dates.map((t) => {
            const row: any[] = [isBarMode ? formatLabDateLabel(new Date(t)) : new Date(t)];
            seriesDescs.forEach((s) => {
                const key = `${t}|${s.location}|${s.testType}`;
                const v = valueByKey.get(key);
                if (v == null || !isAllowed(key)) {
                    row.push(null, null, null);
                } else {
                    if (v > observedMax) observedMax = v;
                    allValues.push(v);
                    const tf = tooltipByKey.get(key);
                    const lim = limitFor(s.location);
                    const overLimit = lim != null && v > lim;
                    const valColor = overLimit ? '#c62828' : '#2d3a4b';
                    const floored = useLogScale && v <= 0;
                    const plotted = floored ? LOG_FLOOR : v;
                    const lines: string[] = [];
                    lines.push(`<div style="font-weight:600;color:#2d3a4b;margin-bottom:2px;">${escapeHtml(s.label)}</div>`);
                    if (tf?.producer) lines.push(`<div style="color:#546e7a;font-size:0.82rem;">${escapeHtml(tf.producer)}</div>`);
                    if (tf?.sample_point) lines.push(`<div style="color:#546e7a;font-size:0.82rem;">Sample: ${escapeHtml(tf.sample_point)}</div>`);
                    lines.push(`<div style="margin-top:4px;font-weight:600;color:${valColor};">${v}${unit ? ' ' + escapeHtml(unit) : ''}${overLimit ? ' ⚠' : ''}</div>`);
                    if (floored) lines.push(`<div style="color:#b08900;font-size:0.78rem;">Shown at axis floor (log scale can't plot 0)</div>`);
                    if (tf?.sampled_by) lines.push(`<div style="color:#78909c;font-size:0.78rem;">Sampled by ${escapeHtml(tf.sampled_by)}</div>`);
                    if (tf?.conclusion) lines.push(`<div style="color:#78909c;font-size:0.78rem;">Conclusion: ${escapeHtml(tf.conclusion)}</div>`);
                    if (tf?.note) lines.push(`<div style="color:#78909c;font-size:0.78rem;font-style:italic;">${escapeHtml(tf.note)}</div>`);
                    const tooltip = `<div style="padding:8px 12px;font-size:0.88rem;max-width:320px;">${lines.join('')}</div>`;
                    row.push(plotted, styleFor(v, lim, chartKind), tooltip);
                }
            });
            return row;
        });

        const sorted = [...allValues].sort((a, b) => a - b);
        const p95 = percentile(sorted, 95);

        // Y axis cap. In bar mode keep the limit caps in view; line mode has no limits.
        const specMax = isBarMode
            ? seriesSpec.reduce((m, s) => Math.max(m, s.deduct ?? 0, s.max ?? 0), 0)
            : 0;
        // Cap at p95 unless showing outliers; always keep the limit caps in view.
        const cap = showOutliers ? Math.max(observedMax, specMax) : Math.max(p95, specMax);
        const yMax = cap > 0 ? cap * 1.15 : undefined;

        // Plotted bars over their own market's maximum (powers the "Over limit" stat).
        // Built from plotted cells, not `samples`, so duplicate sample points per
        // market+date (one bar) aren't double-counted.
        const overLimitSamples: { date: number; location: string; value: number }[] = [];
        dates.forEach((t) => {
            seriesDescs.forEach((s) => {
                const key = `${t}|${s.location}|${s.testType}`;
                if (!isAllowed(key)) return;
                const v = valueByKey.get(key);
                const lim = limitFor(s.location);
                if (v != null && lim != null && v > lim) {
                    overLimitSamples.push({ date: t, location: s.location, value: v });
                }
            });
        });
        overLimitSamples.sort((a, b) => b.date - a.date || b.value - a.value);

        // Summary stats over every plotted value in range
        const count = allValues.length;
        const sum = allValues.reduce((acc, v) => acc + v, 0);
        const stats = {
            count,
            min: count ? sorted[0] : null,
            max: count ? sorted[sorted.length - 1] : null,
            avg: count ? sum / count : null,
            overLimit: overLimitSamples.length,
            dateCount: dates.length,
        };

        // marketId per series so a bar click can open the contributing-leases modal.
        const marketIdByLocation = new Map<string, number | null>();
        locationsForTest.forEach((l) => marketIdByLocation.set(l.label, l.marketId));
        const meta = seriesDescs.map((s) => ({
            isMarket: s.isMarket,
            isField: s.isField,
            location: s.location,
            marketId: marketIdByLocation.get(s.location) ?? null,
        }));

        return {
            chartData: [header, ...body],
            hasData: true,
            seriesMeta: meta,
            yAxisMax: yMax,
            observedMaxRaw: observedMax,
            p95Value: p95,
            stats,
            // Real sample dates → explicit X-axis ticks when there are only a few.
            dateTicks: dates.map((t) => new Date(t)),
            overLimitSamples,
            hasLimit,
            seriesSpec,
        };
    }, [groups, testType, pair, locationsForTest, selected, unit, showOutliers, chartKind, fromMs, toMs, scaleType, lastNSamples, qualitySpecs]);

    const hasOutliers = observedMaxRaw > p95Value && observedMaxRaw > 0;

    const options = useMemo(() => {
        const isBar = chartKind === 'bar';
        const seriesConfig: Record<number, any> = {};
        // One base color per location (Lab + Field share it; Field is a paler shade).
        const colorByLocation = new Map<string, string>();
        let marketColorIdx = 0;
        let leaseColorIdx = 0;
        seriesMeta.forEach((meta) => {
            if (colorByLocation.has(meta.location)) return;
            if (meta.isMarket) {
                colorByLocation.set(meta.location, MARKET_COLORS[marketColorIdx % MARKET_COLORS.length]);
                marketColorIdx++;
            } else {
                colorByLocation.set(meta.location, LEASE_COLORS[leaseColorIdx % LEASE_COLORS.length]);
                leaseColorIdx++;
            }
        });
        seriesMeta.forEach((meta, i) => {
            const base = colorByLocation.get(meta.location) || LEASE_COLORS[0];
            const color = meta.isField ? lighten(base, 0.45) : base;
            seriesConfig[i] = {
                type: isBar ? 'bars' : 'line',
                color,
                lineWidth: isBar ? 0 : (meta.isMarket ? 4 : 1.5),
                pointSize: isBar ? 0 : (meta.isMarket ? 6 : 4),
                // In line mode, Field stays dashed to reinforce the lighter color.
                lineDashStyle: !isBar && meta.isField ? [4, 4] : [],
            };
        });
        // Quality limits are shown ONLY in bar mode (as the HTML overlay caps on each
        // market's bar). Line mode intentionally has no limit lines.

        const useLog = scaleType === 'log';
        const vAxis: any = {
            title: `Result${unit ? ` (${unit})` : ''}`,
            gridlines: { color: '#eef1f5' },
            minValue: useLog ? undefined : 0,
            logScale: useLog,
            textStyle: { color: '#546e7a', fontSize: 11 },
            titleTextStyle: { color: '#2d3a4b', italic: false, fontSize: 12 },
        };
        // Floor below where zero bars plot (0.01) so they stay visible; set even when
        // yAxisMax is undefined, else the Google default collapses them.
        if (useLog) {
            vAxis.viewWindow = { min: 0.001, ...(yAxisMax != null ? { max: yAxisMax } : {}) };
        } else if (yAxisMax != null) {
            vAxis.viewWindow = { min: 0, max: yAxisMax };
        }

        const hAxis: any = {
            title: 'Test Date',
            gridlines: { color: '#eef1f5' },
            textStyle: { color: '#546e7a', fontSize: 11 },
            titleTextStyle: { color: '#2d3a4b', italic: false, fontSize: 12 },
        };
        if (isBar) {
            // Discrete category axis: keep labels upright unless there are many.
            hAxis.slantedText = dateTicks.length > 6;
            hAxis.slantedTextAngle = 30;
        } else {
            hAxis.format = 'MMM dd, yyyy';
            // With few dates Google repeats labels across gridlines — pin one per date.
            if (dateTicks.length > 0 && dateTicks.length <= 8) {
                hAxis.ticks = dateTicks;
                hAxis.gridlines = { ...hAxis.gridlines, count: dateTicks.length };
                const span = dateTicks[dateTicks.length - 1].getTime() - dateTicks[0].getTime();
                const pad = span > 0 ? span * 0.15 : 15 * 24 * 60 * 60 * 1000;
                hAxis.viewWindow = {
                    min: new Date(dateTicks[0].getTime() - pad),
                    max: new Date(dateTicks[dateTicks.length - 1].getTime() + pad),
                };
            }
        }

        // With few clusters a % width deforms the bars, so use a pixel width (~26px
        // per bar); fall back to a % once there are enough clusters.
        let groupWidth: string | number = '72%';
        if (isBar) {
            const clusters = Math.max(1, dateTicks.length);
            const seriesCount = Math.max(1, seriesMeta.length);
            const perCluster = Math.min(140, seriesCount * 26);
            groupWidth = clusters <= 6 ? perCluster : '72%';
        }

        return {
            title: `${(pair ? pair.label : labelForTestType(testType, defMap)) || 'Lab Results'} — ${isBar ? 'Value Comparison' : 'Trend Over Time'}`,
            titleTextStyle: { fontSize: 15, bold: true, color: '#2d3a4b' },
            seriesType: isBar ? 'bars' : 'line',
            hAxis,
            vAxis,
            legend: { position: 'top', alignment: 'end', maxLines: 3, textStyle: { fontSize: 11, color: '#37474f' } },
            tooltip: { isHtml: true, trigger: 'focus' },
            focusTarget: 'datum',
            // Line mode connects data series across missing dates; irrelevant for bars.
            interpolateNulls: !isBar,
            series: seriesConfig,
            bar: { groupWidth },
            chartArea: { width: '82%', height: '64%', left: 70, top: 72 },
            backgroundColor: 'transparent',
            animation: { duration: 400, easing: 'out' },
        };
    }, [seriesMeta, pair, yAxisMax, scaleType, chartKind, testType, unit, dateTicks, defMap]);

    return (
        <div className="labResults-graph-container">
            <div className="labResults-graph-controls">
                <label className="labResults-graph-label">
                    Test Type:
                    <select
                        className="labResults-graph-select"
                        value={testType}
                        onChange={(e) => setTestType(e.target.value)}
                        disabled={!testTypeOptions.length}
                    >
                        {!testTypeOptions.length && <option value="">No data</option>}
                        {useOptgroups
                            ? optionGroups.map(([category, opts]) => (
                                <optgroup key={category} label={category}>
                                    {opts.map((t) => (
                                        <option key={t.value} value={t.value}>
                                            {t.label}
                                        </option>
                                    ))}
                                </optgroup>
                            ))
                            : testTypeOptions.map((t) => (
                                <option key={t.value} value={t.value}>
                                    {t.label}
                                </option>
                            ))}
                    </select>
                </label>

                <div className="labResults-graph-toggle" role="group" aria-label="Chart type">
                    <button
                        type="button"
                        className={`labResults-graph-toggle-btn lr-tip ${chartKind === 'line' ? 'is-active' : ''}`}
                        onClick={() => setChartKind('line')}
                        data-tip="Line chart — trends over time"
                    >
                        📈 Line
                    </button>
                    <button
                        type="button"
                        className={`labResults-graph-toggle-btn lr-tip ${chartKind === 'bar' ? 'is-active' : ''}`}
                        onClick={() => setChartKind('bar')}
                        data-tip="Bar chart — compare exact values on each date"
                    >
                        📊 Bar
                    </button>
                </div>

                <label
                    className="labResults-graph-label lr-tip"
                    data-tip="Y axis scale. Linear: equal spacing — best for similar values. Logarithmic: each step ×10 — use when values span very different magnitudes (e.g. 2 and 5000) so small ones stay visible."
                >
                    Y Scale:
                    <select
                        className="labResults-graph-select"
                        value={scaleType}
                        onChange={(e) => setScaleType(e.target.value as 'linear' | 'log')}
                    >
                        <option value="linear">Linear</option>
                        <option value="log">Logarithmic</option>
                    </select>
                </label>

                <label className="labResults-graph-label">
                    From:
                    <input
                        type="date"
                        lang="en-US"
                        className="labResults-graph-date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') applyDraftRange(); }}
                    />
                </label>
                <label className="labResults-graph-label">
                    To:
                    <input
                        type="date"
                        lang="en-US"
                        className="labResults-graph-date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') applyDraftRange(); }}
                    />
                </label>
                <button
                    type="button"
                    className={`labResults-graph-apply-btn lr-tip ${rangeDirty ? 'is-dirty' : ''}`}
                    onClick={applyDraftRange}
                    disabled={!rangeDirty}
                    data-tip="Apply the selected date range"
                >
                    Apply Range
                </button>
                <div className="labResults-graph-presets" role="group" aria-label="Date presets">
                    <button
                        type="button"
                        className={`labResults-graph-preset-btn lr-tip ${lastNSamples === DEFAULT_LAST_N ? 'is-active' : ''}`}
                        onClick={() => applyPreset('lastN')}
                        disabled={!dateExtent || dateExtent.distinct.length === 0}
                        data-tip={`Show the ${DEFAULT_LAST_N} most recent individual results (default)`}
                    >
                        Last {DEFAULT_LAST_N}
                    </button>
                    <button type="button" className="labResults-graph-preset-btn lr-tip" onClick={() => applyPreset('30d')} data-tip="Last 30 days, ending on the most recent sample">30d</button>
                    <button type="button" className="labResults-graph-preset-btn lr-tip" onClick={() => applyPreset('90d')} data-tip="Last 90 days, ending on the most recent sample">90d</button>
                    <button type="button" className="labResults-graph-preset-btn lr-tip" onClick={() => applyPreset('ytd')} data-tip="Year to date — from January 1st of the most recent sample's year">YTD</button>
                    <button
                        type="button"
                        className={`labResults-graph-preset-btn lr-tip ${!appliedFrom && !appliedTo && lastNSamples == null ? 'is-active' : ''}`}
                        onClick={() => applyPreset('all')}
                        data-tip="Show every result (no date filter)"
                    >
                        All
                    </button>
                </div>

                {hasOutliers && (
                    <button
                        type="button"
                        className="labResults-graph-checkboxes-btn lr-tip"
                        onClick={() => setShowOutliers((v) => !v)}
                        data-tip={`By default the Y axis is capped at the 95th percentile (${p95Value.toFixed(1)}) so one extreme value doesn't flatten the rest. Toggle to extend the axis up to the real max (${fmtNum(observedMaxRaw)}) and show those outliers.`}
                    >
                        {showOutliers ? 'Hide outliers (cap at p95)' : 'Show outliers'}
                    </button>
                )}

                {pair && (
                    <span
                        className="labResults-graph-hint lr-tip"
                        data-tip="This test type was measured both in the lab and in the field. Each location draws two series: the solid line is the lab value, the dashed line is the field value."
                    >
                        Solid = Lab · Dashed = Field ⓘ
                    </span>
                )}
                {hasData && seriesMeta.some((m) => m.isMarket && m.marketId != null) && (
                    <span className="labResults-graph-hint">
                        🛢 Click a market bar/point to see contributing leases
                    </span>
                )}
                {!hasData && (
                    <span className="labResults-graph-empty">
                        No numeric results for the selected test type.
                    </span>
                )}
            </div>

            {locationsForTest.length > 0 && (
                <div className="labResults-graph-checkboxes">
                    <div className="labResults-graph-checkboxes-actions">
                        <button
                            type="button"
                            className="labResults-graph-leases-toggle"
                            onClick={() => setLeasePanelOpen((v) => !v)}
                            aria-expanded={leasePanelOpen}
                            title={`${leasePanelOpen ? 'Hide' : 'Show'} ${locationNoun.toLowerCase()} list`}
                        >
                            {locationNoun}
                            <span className="labResults-graph-producer-count">{leaseCounts.selected}/{leaseCounts.total}</span>
                            <span className="labResults-graph-leases-caret">{leasePanelOpen ? '▲' : '▼'}</span>
                        </button>
                        <input
                            type="text"
                            className="labResults-graph-search"
                            placeholder="Search lease or producer…"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); if (e.target.value) setLeasePanelOpen(true); }}
                        />
                        <button type="button" onClick={selectAll} className="labResults-graph-checkboxes-btn">
                            Select All
                        </button>
                        <button type="button" onClick={clearAll} className="labResults-graph-checkboxes-btn">
                            Clear
                        </button>
                    </div>

                    {leasePanelOpen && (
                    <div className="labResults-graph-producer-groups">
                        {filteredGroups.length === 0 && (
                            <div className="labResults-graph-empty">No {locationNoun.toLowerCase()} match "{search}"</div>
                        )}
                        {filteredGroups.map((grp) => {
                            const collapsed = !!collapsedProducers[grp.producer];
                            const allOn = grp.items.every((l) => selected[l.label]);
                            return (
                                <div
                                    key={(grp.isMarket ? 'M::' : 'L::') + grp.producer}
                                    className={`labResults-graph-producer-group ${grp.isMarket ? 'is-market' : ''}`}
                                >
                                    <div className="labResults-graph-producer-header">
                                        <button
                                            type="button"
                                            className="labResults-graph-producer-toggle"
                                            onClick={() => toggleCollapseProducer(grp.producer)}
                                            title={collapsed ? 'Expand' : 'Collapse'}
                                        >
                                            {collapsed ? '▶' : '▼'}
                                        </button>
                                        <span className="labResults-graph-producer-name">
                                            {grp.producer}
                                        </span>
                                        <span className="labResults-graph-producer-count">
                                            {grp.items.length}
                                        </span>
                                        <button
                                            type="button"
                                            className="labResults-graph-checkboxes-btn"
                                            onClick={() => allOn ? clearAllInProducer(grp.producer) : selectAllInProducer(grp.producer)}
                                        >
                                            {allOn ? `Clear ${grp.producer}` : `Select all from ${grp.producer}`}
                                        </button>
                                    </div>
                                    {!collapsed && (
                                        <div className="labResults-graph-checkboxes-list">
                                            {grp.items.map((l) => {
                                                const visualChecked = !!selected[l.label];
                                                return (
                                                <label
                                                    key={l.label}
                                                    className={`labResults-graph-checkbox ${l.isMarket ? 'labResults-graph-checkbox--market' : 'labResults-graph-checkbox--lease'}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={visualChecked}
                                                        onChange={() => toggleLocation(l.label, !visualChecked)}
                                                    />
                                                    <span>{l.label}</span>
                                                    {l.isMarket && l.marketId != null && (
                                                        <button
                                                            type="button"
                                                            className="labResults-graph-checkboxes-btn labResults-graph-leases-btn"
                                                            title="View the leases that delivered crude to this market (in the selected date range)"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                setContributorsTarget({ marketId: l.marketId as number, label: l.label, anchorDate: null });
                                                            }}
                                                        >
                                                            🛢 Leases
                                                        </button>
                                                    )}
                                                </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    )}
                </div>
            )}

            {hasData && stats.count > 0 && (
                <div className="labResults-graph-stats">
                    <div className="labResults-graph-stat">
                        <span className="labResults-graph-stat-label">Samples</span>
                        <span className="labResults-graph-stat-value">{stats.count}</span>
                    </div>
                    <div className="labResults-graph-stat">
                        <span className="labResults-graph-stat-label">Dates</span>
                        <span className="labResults-graph-stat-value">{stats.dateCount}</span>
                    </div>
                    <div className="labResults-graph-stat">
                        <span className="labResults-graph-stat-label">Min</span>
                        <span className="labResults-graph-stat-value">{fmtNum(stats.min)}{unit ? ` ${unit}` : ''}</span>
                    </div>
                    <div className="labResults-graph-stat">
                        <span className="labResults-graph-stat-label">Avg</span>
                        <span className="labResults-graph-stat-value">{fmtNum(stats.avg)}{unit ? ` ${unit}` : ''}</span>
                    </div>
                    <div className="labResults-graph-stat">
                        <span className="labResults-graph-stat-label">Max</span>
                        <span className="labResults-graph-stat-value">{fmtNum(stats.max)}{unit ? ` ${unit}` : ''}</span>
                    </div>
                    {hasLimit && (
                        <div
                            className={`labResults-graph-stat labResults-graph-stat--overlimit ${stats.overLimit > 0 ? 'is-alert' : 'is-ok'} ${stats.overLimit > 0 ? 'labResults-graph-stat--clickable' : ''}`}
                            onMouseEnter={() => stats.overLimit > 0 && setOverLimitHover(true)}
                            onMouseLeave={() => setOverLimitHover(false)}
                            onClick={() => stats.overLimit > 0 && setOverLimitPinned((v) => !v)}
                            title={stats.overLimit > 0 ? 'Hover to preview · click to pin the full list' : undefined}
                        >
                            <span className="labResults-graph-stat-label">Over limit</span>
                            <span className="labResults-graph-stat-value">
                                {stats.overLimit > 0 ? `${stats.overLimit} ⚠` : '0 ✓'}
                            </span>

                            {(overLimitHover || overLimitPinned) && stats.overLimit > 0 && (
                                <div
                                    className="labResults-overlimit-popover"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="labResults-overlimit-popover-head">
                                        <span>
                                            {overLimitSamples.length} over the market maximum
                                        </span>
                                        {overLimitPinned && (
                                            <button
                                                type="button"
                                                className="labResults-overlimit-close"
                                                onClick={(e) => { e.stopPropagation(); setOverLimitPinned(false); }}
                                                title="Close"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                    <div className="labResults-overlimit-list">
                                        {(overLimitPinned ? overLimitSamples : overLimitSamples.slice(0, 5)).map((s, i) => (
                                            <div key={`${s.date}|${s.location}|${i}`} className="labResults-overlimit-row">
                                                <span className="labResults-overlimit-loc">{s.location}</span>
                                                <span className="labResults-overlimit-date">{formatLabDateLabel(new Date(s.date))}</span>
                                                <span className="labResults-overlimit-val">{fmtNum(s.value)}{unit ? ` ${unit}` : ''}</span>
                                            </div>
                                        ))}
                                    </div>
                                    {!overLimitPinned && overLimitSamples.length > 5 && (
                                        <div className="labResults-overlimit-more">
                                            +{overLimitSamples.length - 5} more · click to see all
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {chartKind === 'bar' && hasData && seriesMeta.some((m) => m.isMarket && m.marketId != null) && (
                <div className="labResults-graph-specs-bar">
                    {showSpecs && (
                        <span className="labResults-graph-specs-legend">
                            <span className="labResults-graph-specs-key">
                                <span className="labResults-graph-specs-swatch" style={{ backgroundColor: DEDUCT_LINE_COLOR }} />
                                Deduct start
                            </span>
                            <span className="labResults-graph-specs-key">
                                <span className="labResults-graph-specs-swatch" style={{ backgroundColor: MAXIMUM_LINE_COLOR }} />
                                Maximum
                            </span>
                        </span>
                    )}
                    <button
                        type="button"
                        className={`labResults-graph-checkboxes-btn ${showSpecs ? 'is-active' : ''}`}
                        onClick={() => { setShowSpecs((v) => !v); setCapTip(null); }}
                    >
                        {showSpecs ? 'Hide Quality Specs' : 'See Quality Specs'}
                    </button>
                </div>
            )}

            <div className="labResults-graph-chart">
                {/* react-google-charts does not reliably redraw when only the data/options
                    change but the column shape stays the same. Keying on the inputs that
                    affect rendering forces a clean remount (chart kind, range, scale, …). */}
                <Chart
                    key={`${chartKind}|${scaleType}|${showOutliers}|${appliedFrom}|${appliedTo}|${testType}|${lastNSamples}`}
                    chartType="ComboChart"
                    width="100%"
                    height="500px"
                    data={chartData}
                    options={options}
                    chartEvents={[
                        {
                            eventName: 'select',
                            callback: ({ chartWrapper }) => {
                                if (!chartWrapper) return;
                                const chart = chartWrapper.getChart();
                                if (!chart) return;
                                const selection = chart.getSelection();
                                if (!selection.length) return;
                                const col = selection[0].column;
                                if (col == null) return;
                                // DataTable layout: col 0 = date, then each series uses
                                // 3 columns (value, style, tooltip). Recover the series index.
                                const seriesIndex = Math.floor((col - 1) / 3);
                                const meta = seriesMeta[seriesIndex];
                                // The clicked row maps to its test date — passed so the modal can
                                // default to the delivery window leading up to that sample.
                                const row = selection[0].row;
                                const anchorDate = (row != null && dateTicks[row])
                                    ? toLabDateYMD(dateTicks[row])
                                    : null;
                                if (meta?.isMarket && meta.marketId != null) {
                                    setContributorsTarget({ marketId: meta.marketId, label: meta.location, anchorDate });
                                }
                            },
                        },
                        {
                            eventName: 'ready',
                            callback: ({ chartWrapper }) => {
                                // Place the overlay caps from the chart's pixel layout
                                // (getBoundingBox per bar + getYLocation for the limit).
                                try {
                                    if (chartKind !== 'bar') {
                                        setLimitMarks((prev) => (prev.length ? [] : prev));
                                        return;
                                    }
                                    const chart: any = chartWrapper?.getChart?.();
                                    const layout = chart?.getChartLayoutInterface?.();
                                    if (!layout || typeof layout.getBoundingBox !== 'function') return;
                                    const u = unit ? ` ${unit}` : '';
                                    const marks: { left: number; top: number; width: number; color: string; market: string; lineLabel: string }[] = [];
                                    for (let i = 0; i < seriesSpec.length; i++) {
                                        const sp = seriesSpec[i];
                                        if (sp.deduct == null && sp.max == null) continue;
                                        const name = seriesMeta[i]?.location ?? '';
                                        for (let j = 0; j < dateTicks.length; j++) {
                                            let box: any = null;
                                            try { box = layout.getBoundingBox(`bar#${i}#${j}`); } catch { box = null; }
                                            if (!box || !box.width) continue;
                                            // Cap overhangs the bar so it reads as a limit line, not a tick.
                                            const overhang = box.width * 0.6;
                                            const left = box.left - overhang;
                                            const width = box.width + overhang * 2;
                                            if (sp.deduct != null) marks.push({ left, top: layout.getYLocation(sp.deduct), width, color: DEDUCT_LINE_COLOR, market: name, lineLabel: `Deduct start: ${sp.deduct}${u}` });
                                            if (sp.max != null) marks.push({ left, top: layout.getYLocation(sp.max), width, color: MAXIMUM_LINE_COLOR, market: name, lineLabel: `Maximum: ${sp.max}${u}` });
                                        }
                                    }
                                    // Bail out when unchanged so 'ready' → setState can't loop.
                                    setLimitMarks((prev) => (JSON.stringify(prev) === JSON.stringify(marks) ? prev : marks));
                                } catch {
                                    /* layout API unavailable — skip caps, never break the chart */
                                }
                            },
                        },
                    ]}
                />
                {showSpecs && limitMarks.map((m, idx) => (
                    <div
                        key={idx}
                        className="lr-limit-cap"
                        style={{ left: m.left, top: m.top, width: m.width, backgroundColor: m.color }}
                        onMouseEnter={() => setCapTip({ left: m.left + m.width / 2, top: m.top, market: m.market, lineLabel: m.lineLabel, color: m.color })}
                        onMouseLeave={() => setCapTip(null)}
                    />
                ))}
                {showSpecs && capTip && (
                    <div className="lr-cap-tip" style={{ left: capTip.left, top: capTip.top }}>
                        <div className="lr-cap-tip-market">{capTip.market}</div>
                        <div className="lr-cap-tip-line" style={{ color: capTip.color }}>{capTip.lineLabel}</div>
                    </div>
                )}
            </div>

            {contributorsTarget && (
                <MarketContributorsModal
                    market={contributorsTarget}
                    anchorDate={contributorsTarget.anchorDate}
                    onClose={() => setContributorsTarget(null)}
                />
            )}
        </div>
    );
};

export default LabResultsGraph;
